<template>
  <div
    class="overflow-hidden"
    :style="viewportStyle"
    aria-hidden="true"
  >
    <div
      class="flex items-center transition-transform duration-300 ease-in-out"
      :style="trackStyle"
    >
      <template v-for="i in count" :key="i - 1">
        <slot :index="i - 1" :active="(i - 1) === activeIndex" />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  count: { type: Number, required: true },
  activeIndex: { type: Number, default: 0 },
  // Max number of dots visible at once; extra dots slide in/out.
  maxVisible: { type: Number, default: 5 },
  // Dot diameter in px (matches Tailwind size-* used by the slot content).
  dotSize: { type: Number, default: 6 },
  // Gap between dots in px (matches Tailwind gap-* used on the track).
  gap: { type: Number, default: 8 },
})

// Small horizontal breathing room so the active dot's scale-up isn't clipped.
const EDGE_PADDING = 2

const pitch = computed(() => props.dotSize + props.gap)
const visibleCount = computed(() => Math.min(props.count, props.maxVisible))

// Index of the first dot shown in the window, centered on the active dot and
// clamped so the window never scrolls past either end.
const windowStart = computed(() => {
  if (props.count <= props.maxVisible) return 0
  const half = Math.floor(props.maxVisible / 2)
  const maxStart = props.count - props.maxVisible
  return Math.min(Math.max(props.activeIndex - half, 0), maxStart)
})

const viewportStyle = computed(() => {
  const width = visibleCount.value * props.dotSize
    + Math.max(visibleCount.value - 1, 0) * props.gap
    + EDGE_PADDING * 2
  // Vertical padding gives the active dot's scale-up room so overflow-hidden
  // (needed for horizontal windowing) doesn't clip it top/bottom.
  return {
    width: `${width}px`,
    padding: `${EDGE_PADDING}px`,
  }
})

const trackStyle = computed(() => ({
  gap: `${props.gap}px`,
  transform: `translateX(${-windowStart.value * pitch.value}px)`,
}))
</script>
